import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The run-up to a deadline, announced three times.
 *
 * Five minutes out is a nudge, one minute out is a warning, and the moment
 * itself is a dialog you have to dismiss. The escalation is the point: a
 * single alert at the due time is either too late to act on or missed
 * entirely, and three identical ones would train people to ignore all of them.
 *
 * Everything here is scheduled in the browser from data the rail already
 * holds. No server timers, no polling loop of its own — the rail refetches on
 * its own schedule and on every notification, and each refetch re-plans what
 * has not fired yet.
 */

export type DueKind = 'task' | 'reminder';
export type DueStage = 'soon' | 'imminent' | 'now';

export interface DueItem {
  id: string;
  title: string;
  /** ISO. Tasks carry dueAt, reminders remindAt; both mean "this moment". */
  at: string;
  kind: DueKind;
}

export interface DueAlert extends DueItem {
  stage: DueStage;
}

/** Minutes before the deadline that each stage fires. Zero is the deadline. */
const STAGES: Array<{ stage: DueStage; leadMs: number }> = [
  { stage: 'soon', leadMs: 5 * 60_000 },
  { stage: 'imminent', leadMs: 60_000 },
  { stage: 'now', leadMs: 0 },
];

/*
 * How far ahead to plan.
 *
 * setTimeout drifts badly over long delays and browsers throttle background
 * tabs, so a timer set eight hours out is not a promise worth making. Anything
 * further away is picked up by a later refetch, by which time it is close.
 */
const HORIZON_MS = 2 * 60 * 60_000;

/*
 * How late a deadline can be and still raise its "now" alert.
 *
 * Opening the app at nine should not replay every deadline missed overnight,
 * but a tab that was asleep for ninety seconds should still say the thing is
 * due. One minute is the width of that gap.
 */
const GRACE_MS = 60_000;

export function useDueAlerts(
  items: DueItem[],
  onAlert: (alert: DueAlert) => void,
  enabled = true,
): void {
  // Fired stages, keyed `${id}:${stage}`. Kept in a ref rather than state so
  // recording one does not re-render and re-run the planner that recorded it.
  const fired = useRef<Set<string>>(new Set());
  const timers = useRef<number[]>([]);

  // Held in a ref so a caller passing an inline function does not re-plan
  // every render.
  const handler = useRef(onAlert);
  handler.current = onAlert;

  // The identity of the work, not the array. Without this, a refetch that
  // returns the same items would tear down and rebuild every timer.
  const signature = items
    .map((i) => `${i.kind}:${i.id}:${i.at}`)
    .sort()
    .join('|');

  useEffect(() => {
    for (const t of timers.current) window.clearTimeout(t);
    timers.current = [];
    if (!enabled) return;

    const now = Date.now();

    for (const item of items) {
      const due = new Date(item.at).getTime();
      if (!Number.isFinite(due)) continue;
      if (due - now > HORIZON_MS) continue;

      for (const { stage, leadMs } of STAGES) {
        const key = `${item.kind}:${item.id}:${stage}`;
        if (fired.current.has(key)) continue;

        const at = due - leadMs;
        const delay = at - now;

        // Already past. Only the deadline itself is worth announcing late, and
        // only just late — the earlier warnings have no meaning once the
        // moment they were warning about has gone.
        if (delay < 0) {
          if (stage === 'now' && delay > -GRACE_MS) {
            fired.current.add(key);
            handler.current({ ...item, stage });
          } else {
            // Mark it so a later refetch does not reconsider it.
            fired.current.add(key);
          }
          continue;
        }

        const timer = window.setTimeout(() => {
          fired.current.add(key);
          handler.current({ ...item, stage });
        }, delay);
        timers.current.push(timer);
      }
    }

    return () => {
      for (const t of timers.current) window.clearTimeout(t);
      timers.current = [];
    };
    // `signature` stands in for `items`; see above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, enabled]);

  // A deadline that moves is a different deadline. Forget what was already
  // announced for it so the new time gets its own run-up.
  useEffect(() => {
    const live = new Set(items.map((i) => `${i.kind}:${i.id}:${i.at}`));
    for (const key of fired.current) {
      const [kind, id, stage] = key.split(':');
      void stage;
      if (![...live].some((l) => l.startsWith(`${kind}:${id}:`))) fired.current.delete(key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);
}

/**
 * The queue behind the "due now" dialog.
 *
 * Two things falling due in the same minute is normal, and a dialog that
 * replaced itself would lose the first one. They wait their turn instead.
 */
export function useDueQueue() {
  const [queue, setQueue] = useState<DueAlert[]>([]);

  const push = useCallback((alert: DueAlert) => {
    setQueue((q) =>
      q.some((a) => a.id === alert.id && a.stage === alert.stage) ? q : [...q, alert],
    );
  }, []);

  const dismiss = useCallback(() => setQueue((q) => q.slice(1)), []);

  return { current: queue[0] ?? null, waiting: Math.max(0, queue.length - 1), push, dismiss };
}
